/**
 * Decision 0144 (experiment 11, 0136 §3): one shape for every ask and update. A card in the order every surface
 * shows it: the question (`ask`), why it matters, the recommendation and its reason (above the answers), each answer
 * with what happens next, who it waits on, details folded. Built from the state; the web renders it (Needs you keeps
 * its richer cards in the same order), the ref lookup sends it to Home and the bell, notifications and push take their
 * title and line from it, and the push action answers with its recommended answer.
 *
 * Pure. Names are people's display names and agents' names; the viewer reads as "you".
 */
import type { CardAnswer, CardShape, ProposalDecision } from "@minga/protocol"
import { recipientOf } from "./multiplayer"
import { askRank } from "./needs-you"
import { reviewOutputs } from "./outputs"
import { isOpenProposal, itemOf } from "./state"
import type { Actor, ProjectState, Proposal } from "./types"

/** A push title holds about this many characters of the ask (rule 7: ≤ 40). */
export const PUSH_TITLE_MAX = 40
/** A push body (rule 7: ≤ 110). */
export const PUSH_BODY_MAX = 110
/** A bell line (rule 7: ≤ 140). */
export const BELL_LINE_MAX = 140

/** At most this many plan changes are decided in place; a bigger plan opens its page (0136 §1). */
export const IN_PLACE_MAX_CHANGES = 5

const firstLine = (text: string): string => (text.trim().split("\n")[0] ?? "").trim()

/** Cut to `max` characters at a word, with an ellipsis. */
export const clip = (text: string, max: number): string => {
  const flat = text.replace(/\s+/g, " ").trim()
  if (flat.length <= max) return flat
  const cut = flat.slice(0, max - 1)
  // a cut that ends a word keeps it; one inside a word goes back to the last space
  const atBoundary = /[\s,;:.!?]/.test(flat[max - 1] ?? " ")
  const space = cut.lastIndexOf(" ")
  return `${(atBoundary || space <= max * 0.6 ? cut : cut.slice(0, space)).replace(/[\s,;:.–-]+$/, "")}…`
}

const LEGACY_SUFFIX = /\n\nNeeds approval: [\s\S]*$/
const norm = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()

const isReview = (p: Proposal): boolean => p.body._tag === "question" && p.intentKey.startsWith("review:")

/** The card kind in people's words (the kicker). */
export const cardKindWord = (p: Proposal): string => {
  const b = p.body
  switch (b._tag) {
    case "plan_change":
      return b.changes.length > 0 && b.changes.every((c) => c._tag === "ReviseBrief")
        ? "Brief edit"
        : "Plan change"
    case "question":
      return b.escalation ? "Stuck" : isReview(p) ? "Review" : "Question"
    case "permission":
      return "Permission"
    case "memory":
      return "Memory"
    case "outward_action":
      return "Outward action"
    case "monitor":
      return "Schedule"
    case "project_request":
      return "Project request"
  }
}

/** A person's or an agent's name; "you" for the viewer; null for Minga's own processes. */
export const cardActorName = (s: ProjectState, a: Actor, viewer: string | null = null): string | null => {
  switch (a._tag) {
    case "user":
      return a.userId === viewer
        ? "you"
        : (s.members.find((m) => m.userId === a.userId)?.displayName ?? "a person")
    case "agent":
      return s.agents.find((x) => x.id === a.agentId)?.name ?? "an agent"
    case "worker":
      return "a worker"
    default:
      return null
  }
}

const personName = (s: ProjectState, userId: string, viewer: string | null): string =>
  userId === viewer ? "you" : (s.members.find((m) => m.userId === userId)?.displayName ?? "a person")

/**
 * The question a person answers: the proposer's `ask` when it wrote one, else derived (never a path or a plan edge
 * when something better is known): a question's first line, a review's "Review DIC-3: …", a stuck item's "DIC-3 is
 * stuck: …", a memory's "Keep a memory note: …?", a plan card's one line.
 */
export const cardAsk = (s: ProjectState, p: Proposal): string => {
  if (p.ask?.trim()) return p.ask.trim()
  const b = p.body
  switch (b._tag) {
    case "plan_change":
      return (
        p.oneLine.trim() ||
        `Approve ${b.changes.length} plan ${b.changes.length === 1 ? "change" : "changes"}?`
      )
    case "question": {
      const subject = b.subject
      const item = subject?._tag === "item" ? itemOf(s, subject.id) : undefined
      if (b.escalation) return item ? `${item.ref} is stuck: ${item.title}` : p.oneLine
      if (isReview(p)) {
        const prompt = firstLine(b.prompt)
        if (item && (!prompt || prompt === `Review ${item.ref} ${item.title}`)) {
          if (b.options.some((o) => o.key === "merge")) return `Merge ${item.title}?`
          return `Review ${item.ref}: ${item.title}`
        }
        return prompt || p.oneLine
      }
      return firstLine(b.prompt) || p.oneLine
    }
    case "permission":
      return b.title ? `Allow: ${b.title}?` : p.oneLine
    case "memory": {
      const t = b.draft?.title
      if (!t) return p.oneLine
      return {
        create: `Keep a memory note: ${t}?`,
        revise: `Update the memory note: ${t}?`,
        archive: `Forget the memory note: ${t}?`,
        promote: `Share the memory note: ${t}?`,
      }[b.op]
    }
    case "outward_action":
      return b.preview.summary || p.oneLine
    case "monitor":
      return b.monitor.instruction ? `Schedule “${b.monitor.instruction}”?` : p.oneLine
    case "project_request":
      return `${b.draft.target.projectName}: ${b.draft.title}`
  }
}

/** Why it matters: the proposer's reasoning (not a system's, not a repeat of the question), at most two lines. */
export const cardWhyLine = (s: ProjectState, p: Proposal): string | null => {
  if (p.proposedBy._tag === "system" || p.proposedBy._tag === "runner") return null
  if (p.body._tag === "permission") return null
  if (p.body._tag === "question" && p.body.escalation) return p.body.escalation.detail.trim() || null
  const why = p.reasoning.replace(LEGACY_SUFFIX, "").trim()
  if (!why) return null
  if (p.body._tag === "question" && norm(why) === norm(p.body.prompt)) return null
  if (norm(why) === norm(cardAsk(s, p))) return null
  return why
}

const planThen = (p: Proposal): string => {
  if (p.body._tag !== "plan_change") return "The changes apply."
  const n = p.body.changes.length
  const questions = p.body.questions?.length ?? 0
  return `${n === 1 ? "The change applies" : `The ${n} changes apply`} to the plan.${
    questions > 0
      ? ` Its ${questions === 1 ? "question takes" : `${questions} questions take`} the recommended answer.`
      : ""
  }`
}

/** The answers of a card, each with what happens next; the recommended one marked. */
export const cardAnswers = (s: ProjectState, p: Proposal): ReadonlyArray<CardAnswer> => {
  const from = cardActorName(s, p.proposedBy) ?? "Minga"
  const rec = p.recommendation?.option ?? null
  const approve = (label: string, then: string): CardAnswer => ({
    key: "approve",
    label,
    outcome: then,
    recommended: rec === "approve",
    decision: { _tag: "approve" },
  })
  const reject = (label: string, then: string): CardAnswer => ({
    key: "reject",
    label,
    outcome: then,
    recommended: rec === "reject",
    decision: { _tag: "reject" },
    destructive: true,
    note: "optional",
  })
  const revise: CardAnswer = {
    key: "request_revision",
    label: "Request changes",
    outcome: `Your note goes to ${from}, who revises the card.`,
    recommended: false,
    decision: null,
    note: "required",
  }
  const b = p.body
  switch (b._tag) {
    case "plan_change":
      return [
        approve("Approve", planThen(p)),
        revise,
        reject("Reject", `Nothing changes; ${from} hears why.`),
      ]
    case "question":
      return b.options.map((o) => ({
        key: o.key,
        label: o.label,
        outcome: o.detail?.trim() || null,
        recommended: rec === o.key,
        decision: { _tag: "answer", optionKey: o.key, text: null } satisfies ProposalDecision,
      }))
    case "memory":
      return [
        approve(
          b.op === "archive" ? "Forget it" : "Remember",
          b.op === "archive" ? "Agents stop loading the note." : "Agents load the note from their next turn.",
        ),
        reject("Not this", "Nothing is saved."),
      ]
    case "monitor":
      return [
        approve("Accept", "The schedule starts; you can pause it any time."),
        reject("Not this", "Nothing is scheduled."),
      ]
    case "permission":
      return [
        approve("Allow", "The run goes on."),
        reject("Deny", "The run is told no and goes on without it."),
      ]
    case "outward_action":
      return [approve("Approve", "It goes out as shown."), reject("Reject", "Nothing goes out.")]
    case "project_request":
      return [
        approve("Send", "The other project's lead gets the request."),
        reject("Not this", "Nothing is sent."),
      ]
  }
}

/** Whether the card is decided in place (popover, brief, bell) or on its page (0136 §1). */
export const decidedInPlace = (s: ProjectState, p: Proposal): boolean => {
  if (!isOpenProposal(p) || p.state !== "open") return false
  const b = p.body
  if (b._tag === "outward_action" || b._tag === "permission" || b._tag === "project_request") return false
  if (b._tag === "plan_change") return b.changes.length <= IN_PLACE_MAX_CHANGES
  // a review with artifacts to pick from, or one to read first
  if (isReview(p)) return reviewOutputs(s, p).length === 0
  return true
}

const decisionVerb = (p: Proposal): string => {
  switch (p.state) {
    case "approved":
    case "applied":
      return p.body._tag === "memory" ? "Remembered" : "Approved"
    case "rejected":
      return "Declined"
    case "answered": {
      const key = p.resolution?.optionKey ?? null
      const label =
        key && p.body._tag === "question" ? p.body.options.find((o) => o.key === key)?.label : undefined
      return label ? `Answered “${label}”` : "Answered"
    }
    case "expired":
      return "Expired"
    case "withdrawn":
      return "Withdrawn"
    case "apply_failed":
      return "Approved, but it could not apply"
    case "adopted":
      return "Adopted"
    default:
      return cardKindWord(p)
  }
}

/** "Approved by you", "Answered “Merge” by Becky"; null while open. */
export const decidedLine = (s: ProjectState, p: Proposal, viewer: string | null): string | null => {
  if (isOpenProposal(p) || !p.resolution) return null
  const by = cardActorName(s, p.resolution.by, viewer)
  const verb = decisionVerb(p)
  return by && p.state !== "expired" && p.state !== "withdrawn" ? `${verb} by ${by}` : verb
}

/**
 * The recommendation: the proposer's (`recommendation`), else the newest note of an agent that passed the card up the
 * chain (decision 0102: its recommendation in words, naming no answer).
 */
const recommendationOf = (
  s: ProjectState,
  p: Proposal,
  answers: ReadonlyArray<CardAnswer>,
): CardShape["recommendation"] => {
  if (p.recommendation) {
    const answer = answers.find((a) => a.key === p.recommendation?.option)
    return {
      by: cardActorName(s, p.proposedBy),
      key: p.recommendation.option,
      label: answer?.label ?? null,
      reason: p.recommendation.reason,
    }
  }
  const last = p.escalations?.at(-1)
  if (last)
    return {
      by: s.agents.find((a) => a.id === last.by)?.name ?? null,
      key: null,
      label: null,
      reason: last.note,
    }
  return null
}

/** Who it waits on, in words ("you", "Becky", "Lea"), and whether that is the viewer. */
const waitsOnOf = (
  s: ProjectState,
  p: Proposal,
  viewer: string | null,
): { name: string; viewer: boolean } => {
  if (p.approver._tag === "agent" || p.approver._tag === "lead") {
    const agentId = p.approver.agentId
    return { name: s.agents.find((a) => a.id === agentId)?.name ?? "an agent", viewer: false }
  }
  const to = recipientOf(s, p)
  return { name: personName(s, to, viewer), viewer: to === viewer }
}

/**
 * The recommended answer, when it may be sent from a notification (push action, bell button): an open card decided in
 * place, waiting on this person, with a recommendation that names an answer a click can send, and neither high risk
 * nor irreversible. Outward actions and permission prompts never (they are not decided in place).
 */
export const notificationAnswer = (s: ProjectState, p: Proposal, viewer: string): CardAnswer | null => {
  if (!p.recommendation || !decidedInPlace(s, p)) return null
  if (p.risk === "high" || p.reversibility === "irreversible") return null
  if (p.approver._tag !== "human" || recipientOf(s, p) !== viewer) return null
  const answer = cardAnswers(s, p).find((a) => a.key === p.recommendation?.option)
  return answer?.decision ? answer : null
}

/** The card in one shape, for `viewer` (a user id, or null for no one in particular). */
export const cardShape = (s: ProjectState, p: Proposal, viewer: string | null): CardShape => {
  const answers = cardAnswers(s, p)
  const waits = waitsOnOf(s, p, viewer)
  const rank = askRank(s, p)
  const chain = (p.escalations ?? []).map(
    (e) => `${s.agents.find((a) => a.id === e.by)?.name ?? "An agent"}: ${e.note}`,
  )
  const reasoning = p.reasoning.replace(LEGACY_SUFFIX, "").trim()
  const decidedText = decidedLine(s, p, viewer)
  return {
    proposalId: p.id,
    ref: p.ref,
    kindWord: cardKindWord(p),
    kind: p.body._tag,
    from: cardActorName(s, p.proposedBy, viewer),
    revision: p.revision,
    contentHash: p.contentHash,
    open: isOpenProposal(p),
    ask: cardAsk(s, p),
    why: cardWhyLine(s, p),
    blocks: Math.floor((rank % 1_000_000) / 1_000),
    recommendation: recommendationOf(s, p, answers),
    answers,
    waitsOn: waits.name,
    waitsOnViewer: waits.viewer,
    since: (p.escalations?.at(-1)?.at ?? p.createdAt) as never,
    details: [reasoning, ...chain].filter((x) => x !== "").join("\n\n"),
    inPlace: decidedInPlace(s, p),
    fromNotification: viewer !== null && notificationAnswer(s, p, viewer) !== null,
    decided: decidedText ? { line: decidedText, at: (p.resolution?.at ?? p.updatedAt) as never } : null,
    decisionRef: p.decisionRef ?? null,
  }
}

/**
 * The one line under a card's title in the bell and push (rule 4: the recommendation before the choices): "Lea
 * recommends Approve: PR 1 doesn't depend on the test." Without a recommendation, why it matters; else nothing.
 */
export const cardLineOf = (shape: Pick<CardShape, "recommendation" | "why">): string | null => {
  const r = shape.recommendation
  if (r) {
    const who = r.by ?? "Minga"
    return r.label ? `${who} recommends ${r.label}: ${r.reason}` : `${who}: ${r.reason}`
  }
  return shape.why ? firstLine(shape.why) : null
}
